/**
 * HFDS — Headzflex Design System
 * Component: HF-013 Reviews Bridge
 * Version: 1.0.0
 *
 * Responsibilities:
 * - Connect review links to the reviews section.
 * - Support dynamically loaded Shopify app blocks.
 * - Emit analytics-ready review navigation events.
 */

(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-hf-reviews-bridge]";
  const REVIEW_TRIGGER_SELECTOR =
    '[data-hf-action="scroll-to-reviews"]';

  class HFReviewsBridge {
    /**
     * @param {HTMLElement} root
     */
    constructor(root) {
      this.root = root;
      this.productId = String(
        root.dataset.productId || ""
      );

      this.handleDocumentClick =
        this.handleDocumentClick.bind(this);

      this.initialize();
    }

    initialize() {
      if (!window.HFDS) {
        console.error(
          "[HFDS Reviews Bridge] HFDS core is unavailable."
        );

        return;
      }

      document.addEventListener(
        "click",
        this.handleDocumentClick
      );

      this.root.dataset.hfInitialized = "true";
    }

    /**
     * Event delegation keeps review triggers working after
     * Shopify sections or app blocks are re-rendered.
     *
     * @param {MouseEvent} event
     */
    handleDocumentClick(event) {
      const trigger = event.target.closest(
        REVIEW_TRIGGER_SELECTOR
      );

      if (!(trigger instanceof HTMLElement)) {
        return;
      }

      event.preventDefault();

      this.scrollToReviews(trigger);
    }

    /**
     * @param {HTMLElement} trigger
     */
    scrollToReviews(trigger) {
      this.root.scrollIntoView({
        behavior: window.HFDS.prefersReducedMotion()
          ? "auto"
          : "smooth",
        block: "start"
      });

      const firstFocusable = this.root.querySelector(
        [
          "button:not([disabled])",
          "a[href]",
          "input:not([disabled])",
          "select:not([disabled])",
          "textarea:not([disabled])",
          '[tabindex]:not([tabindex="-1"])'
        ].join(",")
      );

      window.setTimeout(() => {
        if (firstFocusable instanceof HTMLElement) {
          firstFocusable.focus({
            preventScroll: true
          });
        }
      }, window.HFDS.prefersReducedMotion() ? 0 : 500);

      window.HFDS.emit(
        "reviews:navigate",
        {
          productId: this.productId,
          triggerText:
            trigger.textContent?.trim() || "",
          source:
            trigger.dataset.hfReviewSource || "product"
        },
        this.root
      );

      window.HFDS.announce(
        "Product reviews section reached.",
        "polite"
      );
    }

    destroy() {
      document.removeEventListener(
        "click",
        this.handleDocumentClick
      );

      delete this.root.dataset.hfInitialized;
    }
  }

  function initializeReviewsBridges(
    context = document
  ) {
    if (!window.HFDS) {
      return;
    }

    window.HFDS
      .queryAll(ROOT_SELECTOR, context)
      .forEach((root) => {
        if (!(root instanceof HTMLElement)) {
          return;
        }

        if (root.dataset.hfInitialized === "true") {
          return;
        }

        root.hfReviewsBridge =
          new HFReviewsBridge(root);
      });
  }

  window.HFDS?.ready(() => {
    initializeReviewsBridges(document);
  });

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeReviewsBridges(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      window.HFDS
        ?.queryAll(ROOT_SELECTOR, event.target)
        .forEach((root) => {
          root.hfReviewsBridge?.destroy();
        });
    }
  );

  window.HFDSReviewsBridge = Object.freeze({
    version: "1.0.0",
    initialize: initializeReviewsBridges
  });
})();